import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IoExpand } from 'react-icons/io5';
import { BiCollapseAlt } from 'react-icons/bi';
import { LuPanelRight, LuPanelRightClose } from 'react-icons/lu';
import { selectAuthUser } from '../../store/authSlice';
import ThemeManager from '../../core/ThemeManager';
import { clearReferralUrlCache } from '../../store/atlasSlice';
import { ensureAtlasEmbed } from '../../store/atlasThunks';
import { isBootstrapModalOpen } from '../../utils/dom';
import FloatingPanel from '../markup/floatingPanel/FloatingPanel';
import DockPanel from '../markup/floatingPanel/DockPanel';
import { useFloatingPanel } from '../markup/floatingPanel/useFloatingPanel';
import { useDockPanel } from '../markup/floatingPanel/useDockPanel';
import { getViewportSize } from '../markup/floatingPanel/floatingPanelGeometry';
import AtlasEmbed from './AtlasEmbed';
import activateiqIcon from '../../img/activateiq_icon.png';
import {
	shouldShowAtlasHelper,
	readAtlasWelcomeDismissed,
	writeAtlasWelcomeDismissed,
	getAtlasExpandWindowControl,
	ATLAS_DISPLAY_MODES,
	ATLAS_DEFAULT_DOCK_WIDTH,
	getAtlasFloatingBounds,
	getAtlasDockBounds,
	getAtlasFloatingDefaultGeometry,
	getAtlasFloatingExpandedGeometry,
	setAtlasDockLayoutCssVar,
	clearAtlasDockLayoutCssVar
} from './atlasHelpers';
import './styles.less';

const WELCOME_DELAY_MS = 4000;
const WELCOME_ROTATION_MS = 12000;
const WELCOME_MESSAGES = [
	'Hi, I\'m Atlas. Ask me anything!',
	'I can help with reporting, pacing, campaign setup, and other media-related tasks.',
	'Ask me to find a campaign, placement, or pacing issue.',
	'I can pull reports without leaving this page.'
];

const AtlasHelper = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const authUser = useSelector(selectAuthUser) || {};
	const userId = authUser.id;
	const [displayMode, setDisplayMode] = useState(ATLAS_DISPLAY_MODES.CLOSED);
	const [windowExpanded, setWindowExpanded] = useState(false);
	const [embedMounted, setEmbedMounted] = useState(false);
	const [welcomeDismissed, setWelcomeDismissed] = useState(() => readAtlasWelcomeDismissed(userId));
	const [welcomeReady, setWelcomeReady] = useState(false);
	const [welcomeMessageIndex, setWelcomeMessageIndex] = useState(0);
	const [modalOpen, setModalOpen] = useState(isBootstrapModalOpen);

	const floatingGeometryBeforeExpandRef = useRef(null);

	const panelOpen = displayMode !== ATLAS_DISPLAY_MODES.CLOSED;
	const isFloating = displayMode === ATLAS_DISPLAY_MODES.FLOATING;
	const isDocked = displayMode === ATLAS_DISPLAY_MODES.DOCKED;

	const floatingBounds = useMemo(() => getAtlasFloatingBounds(getViewportSize()), [panelOpen, windowExpanded]);
	const dockBounds = useMemo(() => getAtlasDockBounds(getViewportSize()), [panelOpen, isDocked]);

	const lockedGeometry = windowExpanded && isFloating
		? getAtlasFloatingExpandedGeometry(getViewportSize())
		: null;

	const {
		displayedGeometry,
		isInteracting: isFloatingInteracting,
		resetGeometry,
		setGeometry,
		startDrag,
		startResize
	} = useFloatingPanel({
		enabled: isFloating && panelOpen,
		bounds: floatingBounds,
		defaultGeometry: getAtlasFloatingDefaultGeometry,
		lockedGeometry,
		onResizeStart: () => {
			setWindowExpanded(false);
			floatingGeometryBeforeExpandRef.current = null;
		}
	});

	const {
		dockWidth,
		isInteracting: isDockInteracting,
		resetDockWidth,
		startLeftResize
	} = useDockPanel({
		enabled: isDocked && panelOpen,
		bounds: dockBounds,
		defaultWidth: ATLAS_DEFAULT_DOCK_WIDTH
	});

	const visible = shouldShowAtlasHelper({
		pathname: location.pathname,
		authUser
	});
	const canPromptWelcome = visible && !welcomeDismissed && !panelOpen;
	const showWelcome = canPromptWelcome && welcomeReady && !modalOpen;

	const dismissWelcome = useCallback(() => {
		setWelcomeDismissed(true);
		writeAtlasWelcomeDismissed(userId);
	}, [userId]);

	const resetAtlasPanelSession = useCallback(() => {
		setWindowExpanded(false);
		floatingGeometryBeforeExpandRef.current = null;
		resetGeometry();
		resetDockWidth();
		clearAtlasDockLayoutCssVar();
	}, [resetGeometry, resetDockWidth]);

	const openPanel = useCallback(() => {
		dismissWelcome();
		if (displayMode === ATLAS_DISPLAY_MODES.CLOSED) {
			setWindowExpanded(false);
			floatingGeometryBeforeExpandRef.current = null;
			resetGeometry();
			resetDockWidth();
			clearAtlasDockLayoutCssVar();
		}
		setDisplayMode(ATLAS_DISPLAY_MODES.FLOATING);
		setEmbedMounted(true);
		dispatch(ensureAtlasEmbed()).catch(() => {});
	}, [dispatch, dismissWelcome, displayMode, resetGeometry, resetDockWidth]);

	const closePanel = useCallback(() => {
		setDisplayMode(ATLAS_DISPLAY_MODES.CLOSED);
		resetAtlasPanelSession();
	}, [resetAtlasPanelSession]);

	const dockToRight = useCallback(() => {
		if (windowExpanded) {
			return;
		}
		setWindowExpanded(false);
		setDisplayMode(ATLAS_DISPLAY_MODES.DOCKED);
	}, [windowExpanded]);

	const undockToFloating = useCallback(() => {
		setDisplayMode(ATLAS_DISPLAY_MODES.FLOATING);
		setWindowExpanded(false);
		floatingGeometryBeforeExpandRef.current = null;
		clearAtlasDockLayoutCssVar();
		resetGeometry();
	}, [resetGeometry]);

	const toggleWindowExpanded = useCallback(() => {
		if (!isFloating) {
			return;
		}
		if (windowExpanded) {
			const restore = floatingGeometryBeforeExpandRef.current;
			floatingGeometryBeforeExpandRef.current = null;
			setWindowExpanded(false);
			if (restore) {
				setGeometry(restore);
			}
			return;
		}
		floatingGeometryBeforeExpandRef.current = { ...displayedGeometry };
		setWindowExpanded(true);
	}, [isFloating, windowExpanded, displayedGeometry, setGeometry]);

	useEffect(() => {
		if (isDocked && panelOpen) {
			setAtlasDockLayoutCssVar(dockWidth);
			return;
		}
		clearAtlasDockLayoutCssVar();
	}, [isDocked, panelOpen, dockWidth]);

	useEffect(() => () => clearAtlasDockLayoutCssVar(), []);

	useEffect(() => {
		if (!panelOpen) {
			return;
		}
		const onKeyDown = (event) => {
			if (event.key === 'Escape') {
				closePanel();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [panelOpen, closePanel]);

	useEffect(() => {
		return ThemeManager.subscribeThemeChange(() => {
			setDisplayMode(ATLAS_DISPLAY_MODES.CLOSED);
			resetAtlasPanelSession();
			if (!embedMounted) {
				return;
			}
			dispatch(clearReferralUrlCache());
			setEmbedMounted(false);
		});
	}, [dispatch, embedMounted, resetAtlasPanelSession]);

	useEffect(() => {
		setWelcomeDismissed(readAtlasWelcomeDismissed(userId));
	}, [userId]);

	useEffect(() => {
		if (!canPromptWelcome) {
			setWelcomeReady(false);
			return;
		}
		const timer = setTimeout(() => setWelcomeReady(true), WELCOME_DELAY_MS);
		return () => clearTimeout(timer);
	}, [canPromptWelcome]);

	useEffect(() => {
		if (welcomeDismissed) {
			return;
		}
		setModalOpen(isBootstrapModalOpen());
		const observer = new window.MutationObserver(() => setModalOpen(isBootstrapModalOpen()));
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
		return () => observer.disconnect();
	}, [welcomeDismissed]);

	useEffect(() => {
		if (!showWelcome) {
			return;
		}
		const interval = setInterval(() => {
			setWelcomeMessageIndex((index) => (index + 1) % WELCOME_MESSAGES.length);
		}, WELCOME_ROTATION_MS);
		return () => {
			clearInterval(interval);
			setWelcomeMessageIndex(0);
		};
	}, [showWelcome]);

	if (!visible) {
		return null;
	}

	const expandControl = getAtlasExpandWindowControl(windowExpanded);

	const renderPanelHeader = () => (
		<div className='atlas-helper__panel-header'>
			<span
				className='atlas-helper__panel-title atlas-helper__panel-drag-handle'
				onPointerDown={isFloating ? startDrag : undefined}
			>
				Atlas 2.0
			</span>
			<div className='atlas-helper__panel-actions'>
				{isFloating && (
					<button
						type='button'
						className='atlas-helper__header-icon-btn'
						onClick={toggleWindowExpanded}
						aria-label={expandControl.ariaLabel}
						aria-expanded={windowExpanded}
						data-tip={expandControl.tip}
						data-for='default-tooltip'
					>
						{windowExpanded ? <BiCollapseAlt aria-hidden /> : <IoExpand aria-hidden />}
					</button>
				)}
				{isFloating && !windowExpanded && (
					<button
						type='button'
						className='atlas-helper__header-icon-btn'
						onClick={dockToRight}
						aria-label='Dock Atlas to the right'
						data-tip='Dock to the right'
						data-for='default-tooltip'
					>
						<LuPanelRight aria-hidden />
					</button>
				)}
				{isDocked && (
					<button
						type='button'
						className='atlas-helper__header-icon-btn'
						onClick={undockToFloating}
						aria-label='Undock Atlas window'
						data-tip='Undock window'
						data-for='default-tooltip'
					>
						<LuPanelRightClose aria-hidden />
					</button>
				)}
				<button type='button' className='atlas-helper__header-icon-btn' onClick={closePanel} aria-label='Close Atlas helper'>
					<span className='material-icons'>close</span>
				</button>
			</div>
		</div>
	);

	const embedBody = <AtlasEmbed variant='widget' visible={panelOpen} />;

	return (
		<div className='atlas-helper'>
			{embedMounted && panelOpen && isFloating && (
				<FloatingPanel
					panelClassName='atlas-helper__floating-shell'
					visible={true}
					displayedGeometry={displayedGeometry}
					isInteracting={isFloatingInteracting}
					startResize={startResize}
					header={renderPanelHeader()}
				>
					{embedBody}
				</FloatingPanel>
			)}
			{embedMounted && panelOpen && isDocked && (
				<DockPanel
					panelClassName='atlas-helper__dock-shell'
					visible={true}
					dockWidth={dockWidth}
					isInteracting={isDockInteracting}
					startLeftResize={startLeftResize}
					header={renderPanelHeader()}
				>
					{embedBody}
				</DockPanel>
			)}
			{showWelcome && <div className='atlas-helper__welcome'>
				<button type='button' className='atlas-helper__welcome-message' onClick={openPanel} aria-label='Open Atlas'>
					<span key={welcomeMessageIndex} className='atlas-helper__welcome-text' aria-hidden='true'>
						{WELCOME_MESSAGES[welcomeMessageIndex]}
					</span>
				</button>
				<button type='button' className='atlas-helper__welcome-close' onClick={dismissWelcome} aria-label='Dismiss Atlas message'>
					<span className='material-icons'>close</span>
				</button>
			</div>}
			<button
				type='button'
				className={cx('atlas-helper__launcher', {
					'atlas-helper__launcher--active': panelOpen,
					'atlas-helper__launcher--pulse': showWelcome
				})}
				onClick={panelOpen ? closePanel : openPanel}
				data-tip='Atlas 2.0'
				data-for='default-tooltip'
				aria-label='Atlas 2.0'
				aria-expanded={panelOpen}
			>
				<img src={activateiqIcon} alt='' className='atlas-helper__launcher-icon' />
			</button>
		</div>
	);
};

export default AtlasHelper;
